import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Brain } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { API } from '../../App';
import { toast } from 'sonner';

const MentalHealthTab = ({ user, setResult, setCrisisMessage, setShowCrisisAlert }) => {
  const { t } = useTranslation();
  const [moodLevel, setMoodLevel] = useState(5);
  const [selectedEmotions, setSelectedEmotions] = useState([]);
  const [triggers, setTriggers] = useState('');
  const [loading, setLoading] = useState(false);

  const emotions = [
    t('emotions.happy'), t('emotions.sad'), t('emotions.anxious'), t('emotions.stressed'),
    t('emotions.calm'), t('emotions.excited'), t('emotions.tired'), t('emotions.angry')
  ];

  const handleMentalHealthSubmit = async () => {
    if (selectedEmotions.length === 0) {
      toast.error(t('aiFeatures.pleaseSelectAtLeastOneEmotion'));
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/mental-health/analyze`, {
        mood_level: moodLevel,
        emotions: selectedEmotions,
        triggers: triggers,
        sleep_quality: 'average',
        energy_level: moodLevel
      });
      
      setResult({ type: 'mental-health', data: response.data });
      toast.success(t('aiFeatures.analysisComplete'));
    } catch (error) {
      toast.error('Failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          {t('aiFeatures.moodScale')}
        </label>
        <div className="flex items-center gap-4">
          <span className="text-2xl">😢</span>
          <input
            type="range"
            min="1"
            max="10"
            value={moodLevel}
            onChange={(e) => setMoodLevel(parseInt(e.target.value))}
            className="flex-1 h-3 bg-gradient-to-r from-red-400 via-yellow-400 to-green-400 rounded-lg appearance-none cursor-pointer"
          />
          <span className="text-2xl">😊</span>
          <span className="text-2xl font-bold text-indigo-600 min-w-[3rem] text-center">{moodLevel}/10</span>
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          {t('aiFeatures.currentEmotions')}
        </label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {emotions.map((emotion) => (
            <button
              key={emotion}
              onClick={() => {
                if (selectedEmotions.includes(emotion)) {
                  setSelectedEmotions(selectedEmotions.filter(e => e !== emotion));
                } else {
                  setSelectedEmotions([...selectedEmotions, emotion]);
                }
              }}
              className={`p-3 rounded-lg border text-sm font-medium transition-all transform hover:scale-105 ${
                selectedEmotions.includes(emotion)
                  ? 'bg-indigo-100 border-indigo-500 text-indigo-700 shadow-md'
                  : 'bg-white border-gray-300 hover:border-indigo-300'
              }`}
            >
              {emotion}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          {t('aiFeatures.recentTriggers')}
        </label>
        <textarea
          value={triggers}
          onChange={(e) => setTriggers(e.target.value)}
          placeholder={t('aiFeatures.triggersPlaceholder')}
          className="w-full p-4 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 min-h-[100px]"
        />
      </div>

      <div className="flex gap-3">
        <Button
          onClick={handleMentalHealthSubmit}
          disabled={loading || selectedEmotions.length === 0}
          className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
        >
          {loading ? t('aiFeatures.analyzing') : t('aiFeatures.analyzeWellness')}
        </Button>
        <Button
          onClick={() => toast.info("Check your history in the dedicated page")}
          variant="outline"
          className="border-indigo-600 text-indigo-600"
        >
          {t('tabs.history')}
        </Button>
      </div>
    </div>
  );
};

export default MentalHealthTab;
